import axios from "axios";
import config, { getCurrentBrand } from "./config";
import { isTokenExpired, clearAuthStorage } from "./tokenUtils";

const AUTH_SKIP_PATHS = [
  "/auth/refresh",
  "/auth/register",
  "/auth/email/confirmation",
  "/auth/password-reset",
  "/auth/forgot-password",
];

/** Raw client without auth interceptors — used only for token refresh. */
const refreshClient = axios.create({
  headers: { "Content-Type": "application/json" },
});

let refreshPromise = null;

const getAccessToken = () => localStorage.getItem(config.jwt_key_name);
const getRefreshToken = () => localStorage.getItem("refreshToken");

const shouldSkipAuthRefresh = (requestConfig) => {
  if (requestConfig?.skipAuthRefresh) {
    return true;
  }

  const url = String(requestConfig?.url || "");
  try {
    const path = url.startsWith("http")
      ? new URL(url).pathname
      : url.split("?")[0];
    // Login: .../api/auth (no further segments)
    if (/\/auth\/?$/.test(path)) {
      return true;
    }
  } catch {
    // ignore URL parse errors
  }

  return AUTH_SKIP_PATHS.some((path) => url.includes(path));
};

/**
 * Calls BE refresh endpoint and stores new tokens.
 * Single-flight: concurrent callers share one in-flight promise.
 */
export const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const token = getAccessToken();
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        throw new Error("Missing refresh token");
      }

      const res = await refreshClient.post(
        `${config.base_url}/auth/refresh`,
        { token, refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Brand": getCurrentBrand(),
          },
        },
      );

      const newToken = res?.data?.data?.token;
      const newRefreshToken = res?.data?.data?.refreshToken;

      if (!newToken) {
        throw new Error("Refresh response missing token");
      }

      localStorage.setItem(config.jwt_key_name, newToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      return newToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const redirectToLogin = () => {
  clearAuthStorage();
  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

const attachAuthInterceptors = (client) => {
  client.interceptors.request.use(
    async (requestConfig) => {
      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers["X-Brand"] = getCurrentBrand();

      if (!requestConfig.headers["Content-Type"]) {
        requestConfig.headers["Content-Type"] = "application/json";
      }

      if (shouldSkipAuthRefresh(requestConfig)) {
        return requestConfig;
      }

      let accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken && isTokenExpired(accessToken) && refreshToken) {
        try {
          accessToken = await refreshAccessToken();
        } catch {
          redirectToLogin();
          return Promise.reject(new Error("Session expired"));
        }
      }

      if (accessToken && !isTokenExpired(accessToken)) {
        requestConfig.headers.Authorization = `Bearer ${accessToken}`;
      }

      return requestConfig;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const prevRequest = error?.config;

      if (
        error?.response?.status === 401 &&
        prevRequest &&
        !prevRequest.sent &&
        !shouldSkipAuthRefresh(prevRequest)
      ) {
        prevRequest.sent = true;

        try {
          const newToken = await refreshAccessToken();
          prevRequest.headers = prevRequest.headers || {};
          prevRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(prevRequest);
        } catch (refreshError) {
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );
};

/** Shared API instance (same interceptors as default axios). */
export const api = axios.create({
  baseURL: config.base_url,
  headers: {
    "Content-Type": "application/json",
    "X-Brand": getCurrentBrand(),
  },
});

attachAuthInterceptors(axios);
attachAuthInterceptors(api);

axios.defaults.headers.common["X-Brand"] = getCurrentBrand();

export default api;
